import React from 'react';
import { IResortPageSnowInfo, IResortTrailmapPage } from '@/api/resort/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { useDistanceFormatter, useElevationFormatter } from '@/i18n/unitFormat';
import { SnowMark } from '@/components/common/SnowMark';
import { Text } from '@/components/ui/text';
import { MiniSectionHeading } from './atoms';

export const SnowDepth = ({
  fullResort, trailmap,
}: {
  fullResort: IResortPageSnowInfo;
  trailmap: IResortTrailmapPage | null;
}) => {
  const { t } = useOTSTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');
  const elev = useElevationFormatter();

  const slots = [
    {
      key: 'base',
      label: t('common.base'),
      depth: fullResort.snow?.base,
      elevation: trailmap?.elevation?.base,
    },
    {
      key: 'middle',
      label: t('common.mid'),
      depth: fullResort.snow?.middle,
      elevation: undefined,
    },
    {
      key: 'summit',
      label: t('common.summit'),
      depth: fullResort.snow?.summit,
      elevation: trailmap?.elevation?.summit,
    },
  ].filter((d) => !!d.depth);

  if (slots.length === 0) return null;

  return (
    <section>
      <MiniSectionHeading title={t('resort.skiReport.snowDepth')} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {slots.map((d) => (
          <div key={d.key} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-baseline justify-between gap-2">
              <Text variant="eyebrow">{d.label}</Text>
              {d.elevation ? (
                <Text variant="subtle" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80 tabular-nums">
                  {elev(d.elevation)}
                </Text>
              ) : null}
            </div>
            <div className="mt-1 font-heading text-3xl font-bold leading-none tabular-nums">
              {dt(d.depth)}
            </div>
          </div>
        ))}
      </div>

      {/* Historical-average insight — English copy from the v3 sandbox.
          Translations will be ported separately. */}
      {!!fullResort.depthAverages?.base && !!fullResort.snow?.base && (
        <div className="mt-4 flex items-start gap-3 rounded-md border-l-4 border-primary bg-primary/5 px-4 py-3">
          <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <SnowMark className="h-3.5 w-3.5" />
          </div>
          <Text variant="default" className="text-sm leading-snug">
            {t('resort.skiReport.baseDepthInsight', {
              percent: `${Math.round(100 * (fullResort.snow.base / fullResort.depthAverages.base))}%`,
              typical: dt(fullResort.depthAverages.base),
            })}
          </Text>
        </div>
      )}
    </section>
  );
};
